import styled from 'styled-components';
import { styled as muiStyle, CSSObject, alpha } from '@mui/material/styles';
import { StyledIconButtonProps, StyledSpinnerProps } from '../';

import {
  CircularProgress,
  IconButton as MuiIconButton,
  IconButtonProps as MuiIconButtonProps
} from '@mui/material';

export type ExtendedButtonProps = MuiIconButtonProps & StyledIconButtonProps;

export const Text = styled.div`
  width: 100%;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const Spinner = styled(CircularProgress)<StyledSpinnerProps>(({ theme }) => ({
  position: `absolute`
}));

const variantInheritMixin = ({ theme, variant, color, active }: any): CSSObject => ({
  ...(active && {
    borderColor: alpha(theme.palette.secondary.main, 0.08)
  }),
  ...(variant === `outlined` && {
    border: `1px solid`,
    borderColor: theme.palette.secondary.light,
    backgroundColor: theme.palette.secondary.contrastText,
    '&:hover': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.08)
    },
    '&:active': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.12)
    },
    '&:disabled': {
      // opacity: `0.16`,
      borderColor: alpha(theme.palette.secondary.light, 0.16),
      color: alpha(theme.palette.secondary.light, 0.48)
    },
    ...(active && {
      borderColor: alpha(theme.palette.divider, 0.12)
    })
  }),
  ...(variant === `contained` && {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.common.white,
    '&:hover, &:active': {
      boxShadow: `0 3px 6px 0 rgba(0, 0, 0, 0.32)`,
      backgroundColor: theme.palette.secondary.light
    },
    '&:hover, &:focus, &:active': {
      '.MuiTouchRipple-root': {
        backgroundColor: alpha(theme.palette.common.white, 0.08)
      }
    },
    '&:disabled': {
      backgroundColor: alpha(theme.palette.secondary.light, 0.16),
      color: alpha(theme.palette.secondary.light, 0.48)
    },
    ...(active &&
      {
        // borderColor: alpha(theme.palette.secondary.main, 0.88),
      })
  }),
  ...(variant === `text` && {
    '&:hover': {
      backgroundColor: alpha(theme.palette.secondary.light, 0.08)
    },
    '&:active': {
      backgroundColor: alpha(theme.palette.secondary.light, 0.12)
    },
    '&:disabled': {
      color: alpha(theme.palette.secondary.light, 0.48)
    },
    ...(active && {
      backgroundColor: alpha(theme.palette.divider, 0.12)
    })
  })
});

const variantMixin = ({ theme, variant, color, active }: any): CSSObject => ({
  ...(variant === `outlined` && {
    border: `1px solid`,
    color: theme.palette[color].main,
    borderColor: theme.palette[color].main,
    backgroundColor: theme.palette[color].contrastText,
    '&:hover': {
      color: theme.palette[color].contrastText,
      backgroundColor: theme.palette[color].light,
      borderColor: theme.palette[color].main
    },
    '&:active': {
      color: theme.palette[color].contrastText,
      backgroundColor: theme.palette[color].light,
      borderColor: theme.palette[color].main
    },
    '&:disabled': {
      color: alpha(theme.palette[color].main, 0.7),
      borderColor: alpha(theme.palette[color].main, 0.35)
    },
    ...(active && {
      color: theme.palette[color].contrastText,
      backgroundColor: theme.palette[color].light,
      borderColor: theme.palette[color].main
    })
  }),
  ...(variant === `contained` && {
    color: theme.palette[color].contrastText,
    borderColor: theme.palette[color].main,
    backgroundColor: theme.palette[color].main,
    '&:hover, &:active': {
      boxShadow: `0 3px 6px 0 rgba(0, 0, 0, 0.32)`
    },
    '&:hover': {
      opacity: 1,
      backgroundColor: alpha(theme.palette[color].main, 0.88)
    },
    '&:active': {
      opacity: 1,
      backgroundColor: alpha(theme.palette[color].main, 0.84)
    },
    '&:disabled': {
      color: alpha(theme.palette[color].contrastText, 0.9),
      borderColor: alpha(theme.palette[color].main, 0.35),
      backgroundColor: alpha(theme.palette[color].main, 0.35)
    },
    ...(active && {
      backgroundColor: alpha(theme.palette[color].main, 0.84)
    })
  }),
  ...(variant === `text` && {
    color: theme.palette[color].main,
    '&:disabled': {
      color: alpha(theme.palette[color].main, 0.9)
    },
    ...(active && {
      color: theme.palette[color].main
    })
  }),
  ...(color === 'default' && {
    borderColor: theme.palette.divider,
    '&:hover, &:active': {
      borderColor: theme.palette.divider
    },
    ...(active && {
      borderColor: theme.palette.divider
    })
  })
});
const sizeMixin = (size: string): CSSObject => ({
  ...(size === `small` && {
    width: `24px`,
    height: `24px`
  }),
  ...(size === `medium` && {
    width: `32px`,
    height: `32px`
  }),
  ...(size === `large` && {
    width: `40px`,
    height: `40px`
  })
});

const activeMixin = ({ theme, variant, color, active }): CSSObject => ({
  ...(variant === undefined &&
    color !== undefined &&
    color !== `inherit` && {
      ...(active && {
        backgroundColor: alpha(theme.palette[color].main, 0.04)
      })
    }),
  ...(variant === undefined &&
    color === 'inherit' && {
      ...(active && {
        backgroundColor: theme.palette.default[100]
      })
    })
});

export const StyledIconButton = muiStyle(MuiIconButton, {
  shouldForwardProp: (prop) => !['circled', 'hover', 'loading', 'active'].includes(String(prop)) //(prop) => prop !== 'circled' && prop !== 'loading',
})<ExtendedButtonProps>(({ theme, variant, color, circled, size, active, loading }) => ({
  border: `0px`,
  padding: `0px`,
  borderRadius: `3px`,
  ...(circled !== undefined &&
    circled && {
      borderRadius: `50%`
    }),
  ...(color === undefined && variantInheritMixin({ theme, variant, color, active })),
  ...(color !== undefined &&
    color === `inherit` &&
    variantInheritMixin({ theme, variant, color, active })),
  ...(color !== undefined &&
    color !== `inherit` &&
    variant !== undefined &&
    variantMixin({ theme, variant, color, active })),
  ...(size !== undefined && sizeMixin(size)),
  ...(active !== undefined && activeMixin({ theme, variant, color, active })),
  ...(loading && {
    pointerEvents: `none`,
    '.text': {
      opacity: 0
    }
  })
}));


// export const StyledIconButton = styled(MuiIconButton)<MuiIconButtonProps & IconButtonProps>`
//
//   border-radius: 3px;
//   font-size: 1rem;
//   ${(props) =>
//     props.variant === 'contained' &&
//     `
//     background-color: ${props.color ? props.theme.palette[props.color].dark : props.theme.palette.grayscaleSecondary[400]};
//     &:hover{
//       background-color: ${props.color && props.theme.palette[props.color].dark}80;
//     }
//     svg {
//       fill: ${props.color && props.theme.palette[props.color].contrastText}
//     }
//     `}
//
//   ${(props) =>
//     props.variant === 'outlined' &&
//     `
//         border: 1px solid ${props.color ? props.theme.palette[props.color].dark : props.theme.palette.grayscaleSecondary[400]};
//     `}
//
//   ${(props) =>
//     props.size === 'small' &&
//     `
//       height: 24px;
//       width: 24px;
//     `}
//   ${(props) =>
//     props.size === 'medium' &&
//     `
//       height: 32px;
//       width: 32px;
//     `}
//   ${(props) =>
//     props.size === 'large' &&
//     `
//       height: 40px;
//       width: 40px;
//     `}
//
//
//    ${(props) =>
//     props.loading &&
//     `
//       pointer-events: none;
//       ${Text} {
//         opacity: 0;
//       }
//       .spinner {
//         position: absolute;
//         display: inline-flex;
//         align-items: center;
//       }
//     `}
//
//   ${(props) =>
//   props.circled  &&
//   `
//     border-radius: 100%;
//     `}
// `;

